import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link as RouterLink } from 'react-router-dom';
import { GUIDE_ENTRY_ROUTE } from '@/features/auth/constants';
import { brand } from '@/shared/config/brand';
import { BrandLogo } from '@/shared/components/BrandLogo';

const footerLinks = {
  explore: [
    { label: 'Find Guides', to: '/guides' },
    { label: 'Destinations', to: '/guides' },
    { label: 'Experiences', to: '/guides' },
    { label: 'Become a Guide', to: GUIDE_ENTRY_ROUTE },
  ],
  company: [
    { label: 'About Us', to: '/about' },
    { label: 'Careers', to: '#' },
    { label: 'Press', to: '#' },
    { label: 'Blog', to: '#' },
  ],
  support: [
    { label: 'Help Center', to: '#' },
    { label: 'Safety', to: '#' },
    { label: 'Contact Us', to: '#' },
    { label: 'Community', to: '#' },
  ],
  legal: [
    { label: 'Privacy Policy', to: '/privacy-policy' },
    { label: 'Terms & Conditions', to: '/terms' },
  ],
};

const socialLinks = [
  { icon: FacebookIcon, label: 'Facebook', href: '#' },
  { icon: InstagramIcon, label: 'Instagram', href: '#' },
  { icon: TwitterIcon, label: 'Twitter', href: '#' },
  { icon: YouTubeIcon, label: 'YouTube', href: '#' },
  { icon: LinkedInIcon, label: 'LinkedIn', href: '#' },
];

function FooterColumn({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: 'white',
          letterSpacing: '0.02em',
          mb: 2.5,
        }}
      >
        {title}
      </Typography>
      {links.map((link) => (
        <Typography
          key={link.label}
          component={RouterLink}
          to={link.to}
          sx={{
            display: 'block',
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.6)',
            textDecoration: 'none',
            mb: 1.5,
            transition: 'color 200ms ease',
            '&:hover': { color: 'white' },
          }}
        >
          {link.label}
        </Typography>
      ))}
    </Box>
  );
}

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#0F172A',
        color: 'white',
        pt: { xs: 6, md: 10 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 3 }}>
              <BrandLogo size="md" surface="dark" />
            </Box>
            <Typography
              sx={{
                fontSize: '0.9375rem',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: 1.6,
                mb: 3,
                maxWidth: 300,
              }}
            >
              {brand.description}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
              {socialLinks.map(({ icon: Icon, label, href }) => (
                <IconButton
                  key={label}
                  size="small"
                  component="a"
                  href={href}
                  aria-label={label}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    transition: 'all 200ms ease',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Box>

            <Box sx={{ mb: { xs: 2, md: 0 } }}>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'white',
                  mb: 1.5,
                }}
              >
                Subscribe to our newsletter
              </Typography>
              <Box
                component="form"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  maxWidth: 340,
                  width: '100%',
                }}
              >
                <InputBase
                  placeholder="Enter your email"
                  sx={{
                    flex: 1,
                    width: '100%',
                    px: 2,
                    py: 1.25,
                    fontSize: '0.875rem',
                    color: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'border-color 200ms ease',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.4)',
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#2563EB',
                    px: 2.5,
                    py: 1.25,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: '#1D4ED8',
                    },
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumn title="Explore" links={footerLinks.explore} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumn title="Company" links={footerLinks.company} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumn title="Support" links={footerLinks.support} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <FooterColumn title="Legal" links={footerLinks.legal} />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 5, md: 8 },
            pt: 3,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.4)',
            }}
          >
            © {year} {brand.uiName}. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {[
              { label: 'Privacy Policy', to: '/privacy-policy' },
              { label: 'Terms & Conditions', to: '/terms' },
            ].map((item) => (
              <Typography
                key={item.to}
                component={RouterLink}
                to={item.to}
                sx={{
                  fontSize: '0.8125rem',
                  color: 'rgba(255, 255, 255, 0.4)',
                  textDecoration: 'none',
                  transition: 'color 200ms ease',
                  '&:hover': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
